import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const BulkClientUpload = ({ onUpload, onCancel }) => {
  const [step1Completed, setStep1Completed] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  // Lista de células válidas (las 34 células completas)
  const validCells = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  // Función para generar y descargar la plantilla Excel
  const downloadTemplate = () => {
    const templateData = [
      {
        name: 'Clínica Dental Sonrisa',
        specialty: 'Odontología',
        cell: 'C1GA',
        salesExecutives: 'juan.perez@empresa.com, maria.garcia@empresa.com',
        email: 'contacto@clinicasonrisa.com',
        country: 'México'
      },
      {
        name: 'Centro Médico Familiar',
        specialty: 'Medicina General',
        cell: 'G1M1',
        salesExecutives: 'carlos.lopez@empresa.com',
        email: 'info@centromedico.com',
        country: 'Colombia'
      },
      {
        name: 'Dr. Ana Martínez',
        specialty: 'Pediatría',
        cell: 'C3GA',
        salesExecutives: 'laura.rodriguez@empresa.com',
        email: 'ana.martinez@email.com',
        country: 'España'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Plantilla Clientes');

    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 25 }, // name
      { wch: 20 }, // specialty
      { wch: 10 }, // cell
      { wch: 35 }, // salesExecutives
      { wch: 30 }, // email
      { wch: 15 }  // country
    ];
    worksheet['!cols'] = colWidths;

    // Descargar archivo
    XLSX.writeFile(workbook, 'plantilla_clientes.xlsx');
    setStep1Completed(true);
  };

  // Función para manejar la selección de archivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];

    if (!validTypes.includes(file.type)) {
      setError('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      return;
    }

    setSelectedFile(file);
    setError('');
    processFile(file);
  };

  // Función para procesar el archivo Excel
  const processFile = (file) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setError('El archivo está vacío o no contiene datos válidos');
          setLoading(false);
          return;
        }

        // Validar y procesar datos
        const processedData = validateAndProcessData(jsonData);
        setFileData(processedData.valid);
        setPreviewData(processedData.valid.slice(0, 5)); // Mostrar primeros 5 para preview

        if (processedData.errors.length > 0) {
          setError(`Se encontraron ${processedData.errors.length} errores:\n${processedData.errors.slice(0, 3).join('\n')}${processedData.errors.length > 3 ? '\n...' : ''}`);
        }

        setLoading(false);
      } catch (error) {
        setError('Error al leer el archivo. Asegúrate de que sea un archivo Excel válido.');
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Función para validar y procesar los datos del Excel
  const validateAndProcessData = (data) => {
    const validData = [];
    const errors = [];

    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 porque Excel empieza en 1 y hay header

      // Validar nombre (obligatorio)
      if (!row.name || !row.name.toString().trim()) {
        errors.push(`Fila ${rowNumber}: El nombre del cliente es obligatorio`);
        return;
      }

      // Validar email si existe
      if (row.email && row.email.toString().trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(row.email.toString().trim())) {
          errors.push(`Fila ${rowNumber}: Email inválido (${row.email})`);
        }
      }

      // Procesar célula (solo una célula)
      let processedCell = '';
      if (row.cell && row.cell.toString().trim()) {
        const cellValue = row.cell.toString().trim();
        if (!validCells.includes(cellValue)) {
          errors.push(`Fila ${rowNumber}: Célula inválida (${cellValue})`);
        } else {
          processedCell = cellValue;
        }
      }

      // Procesar ejecutivos de ventas (validar que sean emails)
      let processedExecutives = [];
      if (row.salesExecutives && row.salesExecutives.toString().trim()) {
        const executivesArray = row.salesExecutives.toString().split(/[,;]/).map(exec => exec.trim()).filter(exec => exec);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = executivesArray.filter(email => !emailRegex.test(email));
        
        if (invalidEmails.length > 0) {
          errors.push(`Fila ${rowNumber}: Emails de ejecutivos inválidos (${invalidEmails.join(', ')})`);
        } else {
          processedExecutives = executivesArray;
        }
      }

      // Crear objeto cliente válido
      const client = {
        id: Date.now() + Math.random(),
        name: row.name.toString().trim(),
        specialty: row.specialty ? row.specialty.toString().trim() : '',
        cells: processedCell ? [processedCell] : [], // Para compatibilidad con el sistema existente
        cell: processedCell,
        salesExecutives: processedExecutives,
        salesExecutive: processedExecutives.length > 0 ? processedExecutives[0] : '', // Compatibilidad
        email: row.email ? row.email.toString().trim() : '',
        country: row.country ? row.country.toString().trim() : 'México',
        meetings: []
      };

      validData.push(client);
    });

    return { valid: validData, errors };
  };

  // Función para cargar los clientes con barra de progreso
  const handleUpload = async () => {
    if (fileData.length === 0) {
      setError('No hay datos válidos para cargar');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Iniciando carga...');
    setError('');

    try {
      const totalClients = fileData.length;
      
      // Simular progreso de carga
      for (let i = 0; i <= 100; i += 10) {
        setUploadProgress(i);
        if (i === 0) setUploadStatus('Validando datos...');
        else if (i === 30) setUploadStatus('Conectando con el servidor...');
        else if (i === 60) setUploadStatus(`Cargando clientes (${Math.floor((i/100) * totalClients)}/${totalClients})...`);
        else if (i === 90) setUploadStatus('Finalizando carga...');
        else if (i === 100) setUploadStatus('¡Carga completada exitosamente!');
        
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Llamar a la función de carga
      await onUpload(fileData);
      
      // Mostrar mensaje de éxito y regresar al dashboard después de 2 segundos
      setTimeout(() => {
        setUploading(false);
        // El componente padre (ClientSection) manejará el regreso al dashboard
      }, 2000);

    } catch (error) {
      setUploading(false);
      setError(`Error al cargar los clientes: ${error.message}`);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#0061A8] mb-2">Carga Masiva de Clientes</h2>
        <p className="text-gray-600">Sigue estos pasos para cargar múltiples clientes desde un archivo Excel</p>
      </div>

      {/* Paso 1: Descarga la Plantilla */}
      <div className="mb-8 p-6 border border-gray-200 rounded-xl">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 mt-1">
            <input
              type="checkbox"
              checked={step1Completed}
              onChange={(e) => setStep1Completed(e.target.checked)}
              className="h-5 w-5 text-[#0061A8] rounded focus:ring-[#0061A8] border-gray-300"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">📋 Paso 1: Descarga la Plantilla</h3>
            <p className="text-gray-600 mb-4">
              Descarga el archivo de ejemplo para ver el formato correcto y llenar tus datos.
            </p>
            <button
              onClick={downloadTemplate}
              className="group flex items-center space-x-2 bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white py-3 px-6 rounded-xl hover:from-[#8bc34a] hover:to-[#A8D08D] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-semibold">Descargar Plantilla Excel</span>
            </button>
          </div>
        </div>
      </div>

      {/* Paso 2: Selecciona tu Archivo */}
      <div className="mb-8 p-6 border border-gray-200 rounded-xl">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 bg-[#0061A8] text-white rounded-full flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">📁 Paso 2: Selecciona tu Archivo</h3>
            <p className="text-gray-600 mb-4">
              Selecciona el archivo Excel con los datos de los clientes:
            </p>
            
            <div className="space-y-4">
              <input
                type="file"
                id="excel-file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => document.getElementById('excel-file').click()}
                  disabled={loading}
                  className="group flex items-center space-x-2 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white py-3 px-6 rounded-xl hover:from-[#004d8a] hover:to-[#0061A8] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="font-semibold">
                    {loading ? 'Procesando...' : 'Seleccionar archivo'}
                  </span>
                </button>
                
                <span className="text-gray-500">
                  {selectedFile ? `✅ ${selectedFile.name}` : 'Sin archivos seleccionados'}
                </span>
              </div>
              
              <p className="text-sm text-gray-500">
                Formatos soportados: .xlsx, .xls
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Preview de datos */}
      {previewData.length > 0 && (
        <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-xl">
          <h4 className="text-lg font-semibold text-green-800 mb-4">✅ Vista Previa de Datos</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-green-100">
                  <th className="px-3 py-2 text-left font-medium text-green-800">Nombre</th>
                  <th className="px-3 py-2 text-left font-medium text-green-800">Especialidad</th>
                  <th className="px-3 py-2 text-left font-medium text-green-800">Célula</th>
                  <th className="px-3 py-2 text-left font-medium text-green-800">Ejecutivos</th>
                  <th className="px-3 py-2 text-left font-medium text-green-800">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-green-800">País</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((client, index) => (
                  <tr key={index} className="border-t border-green-200">
                    <td className="px-3 py-2 font-medium">{client.name}</td>
                    <td className="px-3 py-2">{client.specialty || '-'}</td>
                    <td className="px-3 py-2">{client.cell || '-'}</td>
                    <td className="px-3 py-2 text-xs">{client.salesExecutives.join(', ') || '-'}</td>
                    <td className="px-3 py-2">{client.email || '-'}</td>
                    <td className="px-3 py-2">{client.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fileData.length > 5 && (
              <p className="text-sm text-green-600 mt-2">
                ... y {fileData.length - 5} clientes más
              </p>
            )}
          </div>
        </div>
      )}

      {/* Errores */}
      {error && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-red-800 font-medium mb-1">Errores encontrados:</h4>
              <pre className="text-red-700 text-sm whitespace-pre-wrap">{error}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Consejos para una carga exitosa */}
      <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start space-x-3">
          <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-blue-800 font-semibold mb-3">💡 Consejos para una carga exitosa:</h4>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Usa la plantilla descargada como base para tus datos</li>
              <li>• El campo "name" (nombre del cliente) es obligatorio</li>
              <li>• El campo "cell" debe contener solo una célula válida</li>
              <li>• Para múltiples ejecutivos de ventas, separa los emails con coma (,)</li>
              <li>• Los emails de ejecutivos deben ser válidos (ej: usuario@empresa.com)</li>
              <li>• Los campos vacíos se llenarán con valores por defecto</li>
              <li>• Revisa la previsualización antes de cargar</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Barra de progreso de carga */}
      {uploading && (
        <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl">
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-r from-[#0061A8] to-[#004d8a] rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-[#0061A8] mb-2">Cargando Clientes...</h4>
              <p className="text-sm text-gray-600 mb-3">{uploadStatus}</p>
              
              {/* Barra de progreso */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-3 bg-gradient-to-r from-[#0061A8] to-[#A8D08D] rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              
              {/* Porcentaje */}
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-600">Progreso</span>
                <span className="text-sm font-semibold text-[#0061A8]">{uploadProgress}%</span>
              </div>
            </div>
          </div>
          
          {/* Mensaje de éxito */}
          {uploadProgress === 100 && (
            <div className="flex items-center space-x-2 p-3 bg-green-100 border border-green-200 rounded-lg">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-800 font-medium">¡Carga completada! Regresando al dashboard...</span>
            </div>
          )}
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={onCancel}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          onClick={handleUpload}
          disabled={fileData.length === 0}
          className="group flex items-center space-x-2 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white py-3 px-6 rounded-xl hover:from-[#004d8a] hover:to-[#0061A8] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span className="font-semibold">
            📊 Cargar Clientes {fileData.length > 0 && `(${fileData.length})`}
          </span>
        </button>
      </div>
    </div>
  );
};

export default BulkClientUpload;
